import type { WebChatConfig } from "@chatbotgang/web-sdk-core/model";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "@/components/Accordion";
import { ColorPicker } from "@/components/ColorPicker/ColorPicker";
import { AvatarImageUpload } from "@/components/ImageUpload/AvatarImageUpload";
import { Switch } from "@/components/Switch";
import { Typography } from "@/components/Typography";
import { defineStyles } from "@/internal/emotion";
import { useWebChatConfigForm } from "@/pages/channels/hooks/form/useWebChatConfigForm";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { useUploadImageMutation } from "@/resources/attachment/useUploadImageMutation";

const styles = defineStyles({
  section: css({
    backgroundColor: theme.colors.white,
    borderRadius: "8px",
  }),
  sectionTitle: css({
    fontWeight: 500,
  }),
  subsectionTitle: css({
    fontWeight: 500,
  }),
  fieldRow: css({
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  colorFieldsGrid: css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  }),
  colorField: css({
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  }),
  colorPickerWrapper: css({
    width: "100%",
  }),
});

export const BrandIdentityForm: React.FC = function BrandIdentityForm() {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<WebChatConfig>();
  const { channel } = ChannelQueriesContext.useData();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const dialogueId = useMemo(
    () =>
      channel.type === "wccs" && channel.externalChannelId
        ? channel.externalChannelId
        : "",
    [channel.type, channel.externalChannelId],
  );
  const logoUploadMutation = useUploadImageMutation({
    onSuccess: (url: string) => {
      setValue("brandIdentity.logoUrl", url, { shouldDirty: true });
    },
    onSettled: () => {
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
    },
  });
  const avatarUploadMutation = useUploadImageMutation({
    onSuccess: (url: string) => {
      setValue("brandIdentity.serviceAvatarUrl", url, { shouldDirty: true });
    },
    onSettled: () => {
      if (avatarInputRef.current) {
        avatarInputRef.current.value = "";
      }
    },
  });

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>
        <Typography variant="h3" css={styles.sectionTitle}>
          {t("webChatConfig.brandIdentity.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack css={styles.section} gap="16px">
          <Stack gap="8px">
            <Typography variant="h3" css={styles.subsectionTitle}>
              {t("webChatConfig.brandIdentity.image.title")}
            </Typography>
            <Stack gap="8px">
              <div css={styles.fieldRow}>
                <Controller
                  name="brandIdentity.logoEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch checked={field.value} onChange={field.onChange} />
                  )}
                />
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.image.brandLogo")}
                </Typography>
              </div>
              <Typography variant="note">
                {t("webChatConfig.brandIdentity.image.imageRequirements")}
              </Typography>
              <Controller
                name="brandIdentity.logoUrl"
                control={control}
                render={({ field }) => (
                  <AvatarImageUpload
                    inputRef={logoInputRef}
                    value={field.value}
                    onChange={(file) => {
                      if (!dialogueId) return;
                      logoUploadMutation.mutate({
                        feature: "brandLogo",
                        dialogueId,
                        file,
                      });
                    }}
                    onClear={() => {
                      setValue(
                        "brandIdentity.logoUrl",
                        useWebChatConfigForm.defaultWebChatConfig.brandIdentity
                          .logoUrl,
                        { shouldDirty: true },
                      );
                    }}
                    disable={logoUploadMutation.isPending}
                  />
                )}
              />
            </Stack>
            <Stack gap="8px">
              <div css={styles.fieldRow}>
                <Controller
                  name="brandIdentity.serviceAvatarEnabled"
                  control={control}
                  render={({ field }) => (
                    <Switch checked={field.value} onChange={field.onChange} />
                  )}
                />
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.image.supportAvatar")}
                </Typography>
              </div>
              <Controller
                name="brandIdentity.serviceAvatarUrl"
                control={control}
                render={({ field }) => (
                  <AvatarImageUpload
                    inputRef={avatarInputRef}
                    value={field.value}
                    onChange={(file) => {
                      if (!dialogueId) return;
                      avatarUploadMutation.mutate({
                        feature: "userAvatar",
                        dialogueId,
                        file,
                      });
                    }}
                    onClear={() => {
                      setValue(
                        "brandIdentity.serviceAvatarUrl",
                        useWebChatConfigForm.defaultWebChatConfig.brandIdentity
                          .serviceAvatarUrl,
                        { shouldDirty: true },
                      );
                    }}
                    disable={avatarUploadMutation.isPending}
                  />
                )}
              />
            </Stack>
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" css={styles.subsectionTitle}>
              {t("webChatConfig.brandIdentity.mainColor.title")}
            </Typography>
            <div css={styles.colorFieldsGrid}>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.mainColor.headerBackground")}
                </Typography>
                <Controller
                  name="brandIdentity.mainColor.headerBgColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.mainColor.headerBgColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.mainColor.headerText")}
                </Typography>
                <Controller
                  name="brandIdentity.mainColor.headerTextColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.mainColor.headerTextColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.mainColor.buttonBackground")}
                </Typography>
                <Controller
                  name="brandIdentity.mainColor.startChatButtonBgColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.mainColor.startChatButtonBgColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t("webChatConfig.brandIdentity.mainColor.buttonText")}
                </Typography>
                <Controller
                  name="brandIdentity.mainColor.startChatButtonTextColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.mainColor.startChatButtonTextColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </Stack>
          <Stack gap="8px">
            <Typography variant="h3" css={styles.subsectionTitle}>
              {t("webChatConfig.brandIdentity.chatScreen.title")}
            </Typography>
            <div css={styles.colorFieldsGrid}>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t(
                    "webChatConfig.brandIdentity.chatScreen.userMessageBubbleBackground",
                  )}
                </Typography>
                <Controller
                  name="brandIdentity.chatScreen.msgBubbleColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.chatScreen.msgBubbleColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
              <div css={styles.colorField}>
                <Typography variant="body">
                  {t(
                    "webChatConfig.brandIdentity.chatScreen.userMessageBubbleText",
                  )}
                </Typography>
                <Controller
                  name="brandIdentity.chatScreen.msgTextColor"
                  control={control}
                  render={({ field }) => (
                    <div css={styles.colorPickerWrapper}>
                      <ColorPicker
                        color={field.value}
                        onColorChange={field.onChange}
                        width="100%"
                        height="40px"
                        showColorText
                        allowClear
                        defaultColor={
                          useWebChatConfigForm.defaultWebChatConfig
                            .brandIdentity.chatScreen.msgTextColor
                        }
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </Stack>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
